import { useEffect, useRef, useState } from "react";
import { PRIZE_WHEEL, PRIZE_TYPES, WHEEL_TERMS, TID } from "@/constants/testIds";
import { spinRegistrationWheel } from "@/lib/api";
import { Gift, Sparkles, RotateCw, Lock } from "lucide-react";
import { toast } from "sonner";

/**
 * SpinWheel — server-authoritative prize wheel (53 slots).
 *
 * Prize randomness lives on the backend (`POST /api/leads/registration/{id}/spin`).
 * The wheel is animated to land on the server-selected `prize_index`.
 *
 * Guarantees:
 *  1. If the registration is already spun (in DB), backend returns
 *     `already_spun: true` — the UI locks the wheel and shows the existing
 *     prize without animation.
 *  2. All randomness is server-side — client Math.random cannot influence
 *     which prize is awarded.
 *  3. The iPhone 17 slot is displayed for drama; the server never selects it
 *     (see `decoy: true` in PRIZE_TYPES / `_WINNABLE_INDICES` in server.py).
 *  4. If `initialPrize` is provided (from the registration record on mount,
 *     e.g. after a refresh restore), the wheel is locked immediately.
 */
const SIZE = 340;
const RADIUS = SIZE / 2;
const CENTER = RADIUS;

export default function SpinWheel({
  registrationId,
  prizes = PRIZE_WHEEL,
  initialPrize = null,
  onSpun,
}) {
  const [rotation, setRotation] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [prize, setPrize] = useState(initialPrize);
  const [alreadySpun, setAlreadySpun] = useState(Boolean(initialPrize));
  const [saving, setSaving] = useState(false);
  const inFlight = useRef(false);

  const sliceAngle = 360 / prizes.length;

  // If parent passes down a prize later (e.g. registration is re-fetched
  // after refresh), lock the wheel to that prize.
  useEffect(() => {
    if (initialPrize && !prize) {
      setPrize(initialPrize);
      setAlreadySpun(true);
      // Pre-position the wheel so the correct slice sits under the pointer.
      const idx = prizes.findIndex((p) => p.id === initialPrize.id);
      if (idx >= 0) {
        setRotation(-(idx * sliceAngle + sliceAngle / 2));
      }
    }
  }, [initialPrize, prize, prizes, sliceAngle]);

  const spin = async () => {
    if (spinning || prize || alreadySpun || inFlight.current) return;
    if (!registrationId) {
      toast.error("Registration not found. Please refresh and try again.");
      return;
    }
    inFlight.current = true;
    setSpinning(true);
    setSaving(true);

    let serverResponse;
    try {
      serverResponse = await spinRegistrationWheel(registrationId);
    } catch (err) {
      toast.error("Couldn't spin the wheel. Please try again in a moment.");
      setSpinning(false);
      setSaving(false);
      inFlight.current = false;
      return;
    } finally {
      setSaving(false);
    }

    const { prize_index: winningIndex, prize: won, already_spun } = serverResponse;
    // Match server prize back to local visual list (label/color).
    const localMatch =
      prizes[winningIndex] || prizes.find((p) => p.id === won.id) || prizes[0];

    // Rotation math: pointer at 12-o'clock. Slice centers before spin are at
    // (i * sliceAngle) + sliceAngle/2 measured clockwise from top. Final
    // rotation = 360*spins - (i*slice + slice/2). Skip drama if already spun.
    const spins = already_spun ? 0 : 6;
    const target = 360 * spins - (winningIndex * sliceAngle + sliceAngle / 2);
    setRotation(target);

    const animationMs = already_spun ? 400 : 5200;
    setTimeout(() => {
      const finalPrize = {
        id: won.id,
        label: localMatch.label || won.label,
        detail: localMatch.detail || won.description,
        color: localMatch.color,
      };
      setPrize(finalPrize);
      setAlreadySpun(true);
      setSpinning(false);
      onSpun?.(finalPrize);
      inFlight.current = false;
    }, animationMs);
  };

  return (
    <div data-testid={TID.spinWheel} className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
      <div className="relative mx-auto" style={{ width: SIZE, height: SIZE }}>
        {/* Outer glow ring */}
        <div className="absolute inset-[-14px] rounded-full bg-gradient-to-br from-lime/40 via-teal/20 to-transparent blur-xl pointer-events-none" />
        {/* Pointer */}
        <div className="absolute left-1/2 -translate-x-1/2 -top-3 z-20">
          <div className="w-0 h-0 border-l-[14px] border-l-transparent border-r-[14px] border-r-transparent border-t-[24px] border-t-lime drop-shadow-md" />
        </div>
        {/* Center hub */}
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 rounded-full bg-white border-4 border-forest z-10 grid place-items-center">
          <Sparkles size={22} className="text-teal" />
        </div>
        {/* Wheel */}
        <div
          className="absolute inset-0 rounded-full overflow-hidden border-4 border-forest shadow-[0_20px_60px_-20px_rgba(11,74,61,0.6)]"
          style={{
            transform: `rotate(${rotation}deg)`,
            transition: "transform 5s cubic-bezier(0.17, 0.67, 0.24, 1.01)",
          }}
        >
          <svg viewBox={`0 0 ${SIZE} ${SIZE}`} width={SIZE} height={SIZE} className="block">
            {prizes.map((p, i) => {
              const start = (i * sliceAngle - 90) * (Math.PI / 180);
              const end = ((i + 1) * sliceAngle - 90) * (Math.PI / 180);
              const x1 = CENTER + RADIUS * Math.cos(start);
              const y1 = CENTER + RADIUS * Math.sin(start);
              const x2 = CENTER + RADIUS * Math.cos(end);
              const y2 = CENTER + RADIUS * Math.sin(end);
              const largeArc = sliceAngle > 180 ? 1 : 0;
              const d = `M ${CENTER} ${CENTER} L ${x1} ${y1} A ${RADIUS} ${RADIUS} 0 ${largeArc} 1 ${x2} ${y2} Z`;
              // 53 thin slices — no room for text labels; the prize is
              // revealed in the result banner after the spin. Every slice is
              // rendered identically so nothing tips off the visitor about
              // any particular slot's odds.
              return (
                <path
                  key={`${p.id}-${i}`}
                  d={d}
                  fill={p.color}
                  stroke="#F4F4EF"
                  strokeWidth={0.6}
                />
              );
            })}
          </svg>
        </div>
      </div>

      <div className="space-y-5">
        <div>
          <div className="eyebrow text-teal mb-2 inline-flex items-center gap-2">
            <Gift size={14} className="text-teal" /> One free spin
          </div>
          <h2 className="font-display text-forest text-3xl md:text-4xl leading-tight">
            Spin to unlock<br />an event perk.
          </h2>
          <p className="mt-4 text-forest/70 text-sm md:text-[15px] leading-relaxed">
            Every registration gets <strong>one spin, permanently locked to your
            mobile number</strong>. See the full prize list below &mdash; the wheel is
            weighted, so every attendee walks away with something.
          </p>
        </div>

        {/* Prize legend — 6 categories with a color swatch */}
        <ul className="grid grid-cols-1 gap-2 text-[13px]">
          {PRIZE_TYPES.map((t) => (
            <li key={t.id} className="flex items-center gap-3">
              <span
                className="inline-block w-3.5 h-3.5 rounded-sm flex-none"
                style={{ background: t.color }}
                aria-hidden="true"
              />
              <span className="text-forest">{t.label}</span>
            </li>
          ))}
        </ul>

        {!prize && !alreadySpun && (
          <button
            data-testid={TID.spinWheelBtn}
            onClick={spin}
            disabled={spinning}
            className="btn-primary text-base"
          >
            <RotateCw size={16} className={spinning ? "animate-spin" : ""} />
            {spinning ? "Spinning\u2026" : "Spin the wheel"}
          </button>
        )}

        {prize && (
          <div
            data-testid={TID.spinWheelResult}
            className="border-2 border-lime rounded-2xl p-5 md:p-6 bg-forest text-cream fade-up"
          >
            <div className="eyebrow text-lime mb-2 inline-flex items-center gap-2">
              <Sparkles size={14} /> {alreadySpun && initialPrize ? "Already unlocked" : "You won"}
            </div>
            <div className="font-display text-cream text-2xl md:text-3xl leading-tight">
              {prize.label}
            </div>
            <p className="text-cream/80 text-sm mt-3 leading-relaxed">
              {prize.detail || prize.description}
            </p>
            <div className="mt-4 pt-4 border-t border-cream/15 flex items-center justify-between gap-3">
              <span className="text-[11px] text-cream/60 inline-flex items-center gap-1.5">
                <Lock size={11} />
                {saving
                  ? "Saving to your ticket\u2026"
                  : "Locked to your boarding pass. See you on the ridge."}
              </span>
              <span className="text-[11px] text-lime">Redeem in-person at launch</span>
            </div>
          </div>
        )}

        <p className="text-[11px] text-forest/50 leading-relaxed border-t border-forest/10 pt-3">
          <strong className="text-forest/70">Terms:</strong> {WHEEL_TERMS}
        </p>
      </div>
    </div>
  );
}
